import React, { useState, useEffect, useMemo } from "react";
import MetaTags from "react-meta-tags";
import { useSelector } from "react-redux";
import "../featureDesign.scss";
import FeatureKpiHeader from "../FeatureKpiHeader";
import { useRiskTrendHistory, severityCounters } from "../riskTrend";
import FeatureTableCard from "../FeatureTableCard";
import ScanProfileTabPane from "../ScanProfileTabPane";

const SubdomainFinder = () => {
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [featureNameID, setfeatureNameID] = useState("");
  const [ScanProfileId, setScanProfileId] = useState("");
  const [Checks, setChecks] = useState([]);
  const [activeTab, setActiveTab] = useState("data");

  const [criticalCardPrice, setCriticalCardPrice] = useState(0);
  const [highCardPrice, setHighCardPrice] = useState(0);
  const [mediumCardPrice, setMediumCardPrice] = useState(0);
  const [lowCardPrice, setLowCardPrice] = useState(0);
  const [informationalCardPrice, setInformationalCardPrice] = useState(0);

  const userID = localStorage.getItem("userID");
  const orgID = localStorage.getItem("orgID");
  const token = localStorage.getItem("token");
  const siteDomain = localStorage.getItem("domain") || "scanme.nmap.org";

  const { layoutMode } = useSelector((state) => ({
    layoutMode: state.Layout.layoutMode,
  }));
  const isDark =
    layoutMode === "dark" ||
    (typeof document !== "undefined" && document.body.getAttribute("data-layout-mode") === "dark");
  const theme = isDark ? "dark" : "light";

  // Real per-day history for the Risk trend chart
  const trendHistory = useRiskTrendHistory(165112129704687, severityCounters.subdomain, loading);

  useEffect(() => {
    async function checkAuthAndLoad() {
      try {
        if (token) {
          const authRes = await fetch(`${process.env.REACT_APP_DEFAULTPATH}jwtauth`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          });
          const authData = await authRes.json();
          if (authData && authData.status === false) {
            window.location.href = "/login";
            return;
          }
        }
      } catch (e) {
        console.warn("Auth check error:", e);
      }
      getSubdomainFinderData();
      getScanProfileString();
    }
    checkAuthAndLoad();
  }, []);

  async function getSubdomainFinderData() {
    setLoading(true);
    const featureIDForServer = 165112129704687;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}taskData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userID, orgID, featureIDForServer }),
      });
      const data = await response.json();
      if (data && data.data && Array.isArray(data.data.Data) && data.data.Data.length > 0) {
        const raw = data.data.Data;
        let c = 0, h = 0, m = 0, l = 0, info = 0;
        raw.forEach((item) => {
          if (!item) return;
          const r = String(item.Risk || "").toLowerCase();
          if (r === "critical") c++;
          else if (r === "high") h++;
          else if (r === "medium" || r === "med") m++;
          else if (r === "low") l++;
          else info++;
        });

        setCriticalCardPrice(c);
        setHighCardPrice(h);
        setMediumCardPrice(m);
        setLowCardPrice(l);
        setInformationalCardPrice(info);
        setProductData(raw);
      } else {
        setProductData([]);
      }
    } catch (err) {
      console.error("Subdomain fetch error:", err);
    } finally {
      setLoading(false);
    }
  }

  async function getScanProfileString() {
    const featureIDForServer = 165112129704687;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}updatesubdomaintable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userID, orgID, featureIDForServer }),
      });
      const data = await response.json();
      if (data && data.data) {
        setScanProfileId(data.data.ScanProfileID || "");
        setfeatureNameID(data.data.FeatureName || "");
        setChecks(data.data.Checks || []);
      }
    } catch (err) {
      console.warn("ScanProfile fetch error:", err);
    }
  }

  async function addCheck(keyword, risk) {
    try {
      await fetch(`${process.env.REACT_APP_DEFAULTPATH}createsubdomaintable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orgID,
          string: keyword,
          risk,
          id: ScanProfileId,
        }),
      });
      getScanProfileString();
    } catch (e) {
      console.error("Add check error:", e);
    }
  }

  async function saveCheck(id, keyword, risk) {
    try {
      await fetch(`${process.env.REACT_APP_DEFAULTPATH}saverisksubdomain`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orgID,
          id: ScanProfileId,
          stringID: id,
          risk,
        }),
      });
      getScanProfileString();
    } catch (e) {
      console.error("Save check error:", e);
    }
  }

  async function deleteCheck(id, keyword) {
    try {
      await fetch(`${process.env.REACT_APP_DEFAULTPATH}deletesubdomainstring`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: ScanProfileId,
          string: keyword,
        }),
      });
      getScanProfileString();
    } catch (e) {
      console.error("Delete check error:", e);
    }
  }

  const getBadgeClass = (sev) => {
    switch (String(sev).toLowerCase()) {
      case "critical": return "feature-badge badge-critical";
      case "high": return "feature-badge badge-high";
      case "medium":
      case "med": return "feature-badge badge-medium";
      case "low": return "feature-badge badge-low";
      case "informational":
      case "info": return "feature-badge badge-info";
      default: return "feature-badge badge-info";
    }
  };

  // Score & Severity Calculation
  const totalFindings =
    criticalCardPrice + highCardPrice + mediumCardPrice + lowCardPrice + informationalCardPrice;

  const { score, riskLabel } = useMemo(() => {
    if (totalFindings === 0) return { score: 100, riskLabel: "Low" };
    const deduction =
      criticalCardPrice * 12 + highCardPrice * 6 + mediumCardPrice * 2 + lowCardPrice * 1;
    const rawScore = 100 - deduction;

    let calculatedScore = 98.42;
    let label = "Informational";

    if (criticalCardPrice > 0) {
      calculatedScore = Math.max(15, Math.min(39, rawScore));
      label = "Critical";
    } else if (highCardPrice > 0) {
      calculatedScore = Math.max(40, Math.min(59, rawScore));
      label = "High";
    } else if (mediumCardPrice > 0) {
      calculatedScore = Math.max(60, Math.min(84, rawScore));
      label = "Medium";
    } else if (lowCardPrice > 0) {
      calculatedScore = 98.42;
      label = "Low";
    } else {
      calculatedScore = 98.42;
      label = "Low";
    }

    return { score: calculatedScore, riskLabel: label };
  }, [totalFindings, criticalCardPrice, highCardPrice, mediumCardPrice, lowCardPrice]);

  const columns = [
    { key: "ID", label: "ID", isId: true },
    {
      key: "Subdomain",
      label: "Subdomain",
      isMono: true,
      render: (val, r) => val || r.SubDomain || r.domain || "-",
    },
    { key: "IP", label: "IP", isMono: true, render: (val) => val || "-" },
    {
      key: "Risk",
      label: "Risk",
      render: (val) => <span className={getBadgeClass(val)}>{val || "Informational"}</span>,
    },
  ];

  return (
    <React.Fragment>
      <MetaTags>
        <title>Exposed Subdomains | Glogix</title>
      </MetaTags>

      <div className="page-content feature-page-root" data-theme={theme}>
        <div className="feature-container">
          <FeatureKpiHeader
            title="Subdomain Details"
            breadcrumbItem="Subdomain Details"
            siteTitle={siteDomain}
            score={score}
            riskLabel={riskLabel}
            counts={{
              critical: criticalCardPrice,
              high: highCardPrice,
              medium: mediumCardPrice,
              low: lowCardPrice,
              info: informationalCardPrice,
            }}
            loading={loading}
            trendHistory={trendHistory}
          />

          <FeatureTableCard
            tabs={[
              { key: "data", label: "Exposed Subdomains", count: productData.length },
              { key: "profile", label: "Scan Profile" },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            columns={columns}
            data={productData}
            searchKeys={["ID", "Subdomain", "IP", "Risk"]}
            profileContent={
              <ScanProfileTabPane
                featureName={`Exposed Subdomains - ${featureNameID || "165112129704687"}`}
                checks={Checks}
                onDelete={deleteCheck}
                onSave={saveCheck}
                onAdd={addCheck}
              />
            }
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default SubdomainFinder;
